import { ExtendedComment } from './types';

/**
 * Utility functions for comment state operations
 */

/**
 * Finds a comment by ID and returns both the comment and its index
 */
export const findCommentById = (
  comments: ExtendedComment[],
  commentId: string
): { comment: ExtendedComment; index: number } | null => {
  const index = comments.findIndex(comment => comment.id === commentId);
  return index !== -1 ? { comment: comments[index], index } : null;
};

/**
 * Finds a reply within a comment by ID
 */
export const findReplyById = (
  comment: ExtendedComment,
  replyId: string
): { reply: ExtendedComment; index: number } | null => {
  if (!comment.replies) return null;

  const index = comment.replies.findIndex(reply => reply.id === replyId);
  return index !== -1 ? { reply: comment.replies[index], index } : null;
};

/**
 * Updates a comment by ID with a new comment object
 */
export const updateCommentById = (
  comments: ExtendedComment[],
  commentId: string,
  updater: (comment: ExtendedComment) => ExtendedComment
): ExtendedComment[] => {
  const result = findCommentById(comments, commentId);
  if (!result) return comments;

  const newComments = [...comments];
  newComments[result.index] = updater(result.comment);
  return newComments;
};

/**
 * Updates a reply within a comment by ID
 */
export const updateReplyById = (
  comments: ExtendedComment[],
  parentCommentId: string,
  replyId: string,
  updater: (reply: ExtendedComment) => ExtendedComment
): ExtendedComment[] => {
  return updateCommentById(comments, parentCommentId, parentComment => {
    if (!parentComment.replies) return parentComment;

    const replyResult = findReplyById(parentComment, replyId);
    if (!replyResult) return parentComment;

    const newReplies = [...parentComment.replies];
    newReplies[replyResult.index] = updater(replyResult.reply);

    return {
      ...parentComment,
      replies: newReplies
    };
  });
};

/**
 * Adds a reply to a parent comment
 */
export const addReplyToComment = (
  comments: ExtendedComment[],
  parentCommentId: string,
  newReply: ExtendedComment
): ExtendedComment[] => {
  return updateCommentById(comments, parentCommentId, parentComment => ({
    ...parentComment,
    replies: [...(parentComment.replies || []), newReply]
  }));
};

/**
 * Removes a comment by ID
 */
export const removeCommentById = (
  comments: ExtendedComment[],
  commentId: string
): ExtendedComment[] => {
  const index = comments.findIndex(comment => comment.id === commentId);
  if (index === -1) return comments; // Comment not found, return original array

  // Create copy and use splice for better performance
  const newComments = [...comments];
  newComments.splice(index, 1);
  return newComments;
};

/**
 * Removes a reply from a parent comment
 */
export const removeReplyFromComment = (
  comments: ExtendedComment[],
  parentCommentId: string,
  replyId: string
): ExtendedComment[] => {
  return updateCommentById(comments, parentCommentId, parentComment => ({
    ...parentComment,
    replies: parentComment.replies?.filter(reply => reply.id !== replyId) || []
  }));
};

/**
 * Updates comment count for a specific filter option and its items
 * Uses a more functional approach with better separation of concerns
 */
export const updateFilterCommentCount = (
  filterOptions: any[],
  section: string,
  sectionCategory: string,
  increment: number
): any[] => {
  return filterOptions.map(filterOption => {
    // Early return if this is not the target section
    if (filterOption.label !== section) {
      return filterOption;
    }

    // Create updated filter option with new comment count
    const updatedFilterOption = {
      ...filterOption,
      commentCount: Math.max(0, (filterOption.commentCount || 0) + increment)
    };
    // Update items if they exist
    if (updatedFilterOption.children?.length) {
      updatedFilterOption.children = filterOption.children.map(item =>
        item.label === sectionCategory
          ? {
              ...item,
              commentCount: Math.max(0, (item.commentCount || 0) + increment)
            }
          : item
      );
    }
    return updatedFilterOption;
  });
};

/**
 * Updates the resolved count by finding the item with label 'Resolved'
 * This function specifically handles resolved count updates
 */
export const updateMarkAsResolvedCount = (
  filterOptions: any[],
  increment: number
): any[] => {
  return filterOptions.map(item => {
    return item.label === 'Resolved'
      ? {
          ...item,
          commentCount: Math.max(0, (item.commentCount || 0) + increment)
        }
      : item;
  });
};

// handle key down
export const handleKeyDown = e => {
  if (e.key === 'Enter' || e.key === 'Tab') {
    return true;
  }
  return false;
};

export function isEmptyHtml(html) {
  if (!html) {
    return true;
  }
  // Create a temporary DOM element
  const div = document.createElement('div');
  div.innerHTML = html;

  // Extract text content and trim
  const text = div.textContent || div.innerText || '';

  // Check if text length > 0
  return text.trim().length === 0;
}
export function stripHtml(html) {
  const div = document.createElement('div');
  div.innerHTML = html;
  return div.textContent || div.innerText || '';
}
